import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './DirectorioUniversidades.module.css';

const VideosRecorridos = () => {
    const [selectedUniversity, setSelectedUniversity] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [universities, setUniversities] = useState([]);

    useEffect(() => {
        const fetchUniversidades = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/universidades/obtener-universidades`);
                if (!response.ok) {
                    throw new Error('Error al obtener las carreras');
                }
                const data = await response.json();
                setUniversities(data);
                setLoading(false);
            } catch (err) {
                setError(err.message);
                setLoading(false);
            }
        };
        fetchUniversidades();
    }, []);

    if (loading) {
        return (
        <div className={styles.loadingContainer}>
            <div className={styles.loadingContent}>
            <div className={styles.loadingSpinner}></div>
            <p className={styles.loadingText}>Cargando universidades...</p>
            </div>
        </div>
        );
    }

    if (error) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingContent}>
                    <p className={styles.loadingText}>Error: {error}</p>
                </div>
            </div>
        );
    }

    const handleCardClick = (university) => {
        setSelectedUniversity(university);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedUniversity(null);
    };

    const VideoEmbed = ({ url, className, onLoad, onError }) => {
        const getEmbedUrl = (originalUrl) => {
            // Google Drive
            const driveRegex = /(?:https?:\/\/)?(?:www\.)?drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/;
            const driveMatch = originalUrl.match(driveRegex);
            
            if (driveMatch && driveMatch[1]) {
                return `https://drive.google.com/file/d/10nOntSPBMCEr3QjzAS3kuSx7b8pT5B3C/preview${driveMatch[1]}/preview`;
            }
            
            // Canva
            const canvaRegex = /(?:https?:\/\/)?(?:www\.)?canva\.com\/design\/([a-zA-Z0-9_-]+)\/([a-zA-Z0-9_-]+)\/watch/;
            const canvaMatch = originalUrl.match(canvaRegex);
            
            if (canvaMatch && canvaMatch[1] && canvaMatch[2]) {
                const designId = canvaMatch[1];
                const viewId = canvaMatch[2];
                return `https://www.canva.com/design/${designId}/${viewId}/view?embed`;
            }
            
            // YouTube
            const youtubeRegex = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/;
            const youtubeMatch = originalUrl.match(youtubeRegex);
            
            if (youtubeMatch && youtubeMatch[1]) {
                return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
            }
            
            // Vimeo
            const vimeoRegex = /vimeo\.com\/(\d+)/;
            const vimeoMatch = originalUrl.match(vimeoRegex);
            
            if (vimeoMatch && vimeoMatch[1]) {
                return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
            }
            
            return originalUrl;
        };

        const handleIframeLoad = () => {
            // Dar un delay para asegurar que el contenido se carga completamente
            setTimeout(() => {
                if (onLoad) onLoad();
            }, 2000);
        };

        const handleIframeError = () => {
            if (onError) onError();
        };

        return (
            <iframe
                className={className}
                src={getEmbedUrl(url)}
                allow="autoplay; encrypted-media"
                frameBorder="0"
                allowFullScreen
                onLoad={handleIframeLoad}
                onError={handleIframeError}
            />
        );
    };

    const UniversityCard = ({ university }) => {
        return (
            <div
                className={styles.universityCard}
                onClick={() => handleCardClick(university)}
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCardClick(university);
                    }
                }}
            >
                <h3 className={styles.universityCardTitle}>{university.name}</h3>
                <img
    alt={`Logo de ${university.name}`}
    src={
        university.logo 
            ? `${process.env.REACT_APP_API_URL}/universidades/logos-universidades/${university.logo}`
            : 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzZiNzI4MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkxvZ28gbm8gZGlzcG9uaWJsZTwvdGV4dD48L3N2Zz4='
    }
    className={styles.universityCardLogo}
    onError={(e) => {
        console.error(`Error cargando logo de: ${university.name}`);
        e.target.onerror = null; // 🛑 Detiene loops infinitos
        e.target.src =
            'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzZiNzI4MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkxvZ28gbm8gZGlzcG9uaWJsZTwvdGV4dD48L3N2Zz4=';
    }}
/>
            </div>
        );
    };

    const hasValidVideo = (video) => {
        return video && 
            video !== null && 
            video !== undefined && 
            typeof video === 'string' && 
            video.trim().length > 0;
    };

    const Modal = ({ isOpen, university, onClose }) => {
        const [isVideoLoading, setIsVideoLoading] = useState(true);

        useEffect(() => {
            if (university && hasValidVideo(university.video)) {
                setIsVideoLoading(true);
            }
        }, [university]);

        const handleVideoLoad = () => {
            setIsVideoLoading(false);
        };

        const handleVideoError = () => {
            setIsVideoLoading(false);
        };

        if (!isOpen || !university) return null;

        return (
            <div className={styles.modalOverlay} onClick={onClose}>
                <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modalHeader}>
                        <button
                            className={styles.modalClose}
                            onClick={onClose}
                            type="button"
                            aria-label="Cerrar modal"
                        >
                            ×
                        </button>
                    </div>

                    <div className={styles.modalBody}>
                        <h2 className={styles.modalTitle}>{university.name}</h2>
                        {hasValidVideo(university.video) ? (
                            <div>
                                {isVideoLoading && (
                                    <div className={styles.loadingContainer}>
                                        <div className={styles.spinner}></div>
                                        <p>Cargando video...</p>
                                    </div>
                                )}
                                <VideoEmbed 
                                    url={university.video.trim()} 
                                    className={styles.videoUrl}
                                    onLoad={handleVideoLoad}
                                    onError={handleVideoError}
                                />
                            </div>
                        ) : (
                            <div className={styles.noVideoContainer}>No hay video disponible</div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.container}>
            <div className={styles.wrapper}>
                <div className={styles.content}>
                    <div className={styles.grid}>
                        {universities.map((university) => (
                            <UniversityCard key={university.id} university={university} />
                        ))}
                    </div>
                </div>
            </div>

            <Modal
                isOpen={isModalOpen}
                university={selectedUniversity}
                onClose={closeModal}
            />
        </div>
    );
};

export default VideosRecorridos;